import React, { useState } from "react";
import Modal from "@/components/chat/Modal";
import { toast } from "sonner";
import { EyeOff } from "lucide-react";
import { hasLock, setPin, clearLock } from "@/lib/appLock";
import { hidePreviewsOn, setHidePreviews } from "@/lib/prefs";

export default function AppLockDialog({ onClose }) {
  const [enabled, setEnabled] = useState(hasLock());
  const [hide, setHide] = useState(hidePreviewsOn());
  const [step, setStep] = useState(enabled ? "manage" : "set"); // set | confirm | manage
  const [first, setFirst] = useState("");
  const [entry, setEntry] = useState("");

  const onDigits = (v) => setEntry(v.replace(/\D/g, "").slice(0, 4));

  const submit = async () => {
    if (entry.length !== 4) return toast.error("PIN must be 4 digits");
    if (step === "set") {
      setFirst(entry);
      setEntry("");
      setStep("confirm");
    } else if (step === "confirm") {
      if (entry !== first) {
        toast.error("PINs do not match");
        setEntry(""); setFirst(""); setStep("set");
        return;
      }
      await setPin(entry);
      setEnabled(true);
      setStep("manage");
      setEntry(""); setFirst("");
      toast.success("App Lock enabled");
    }
  };

  const turnOff = () => {
    clearLock();
    setEnabled(false);
    setStep("set");
    toast.success("App Lock turned off");
  };

  const toggleHide = () => {
    const v = !hide;
    setHide(v);
    setHidePreviews(v);
  };

  return (
    <Modal title="App Lock" onClose={onClose}>
      <div className="space-y-4" data-testid="app-lock-dialog">
        <p className="text-sm text-[var(--rc-muted)]">Require a PIN to open RoyalChat Nova on this device — useful on shared phones.</p>

        {step !== "manage" ? (
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3">
            <label className="text-sm font-medium">{step === "set" ? "Choose a 4-digit PIN" : "Confirm your PIN"}</label>
            <input type="password" inputMode="numeric" autoFocus value={entry}
              onChange={(e) => onDigits(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="••••" data-testid="pin-entry"
              className="w-full text-center tracking-[0.6em] text-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--rc-gold)]/50" />
            <button onClick={submit} data-testid="pin-submit" className="w-full rounded-xl bg-[var(--rc-gold)] text-black py-2.5 font-semibold">
              {step === "set" ? "Continue" : "Enable App Lock"}
            </button>
          </div>
        ) : (
          <div className="rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">App Lock is <span className="text-[var(--rc-gold)]">ON</span></span>
              <button onClick={() => { setStep("set"); setEntry(""); setFirst(""); }} data-testid="change-pin-btn" className="text-xs rounded-full px-3 py-1.5 border border-[var(--rc-border)] text-[var(--rc-muted)]">Change PIN</button>
            </div>
            <button onClick={turnOff} data-testid="turn-off-lock-btn" className="w-full rounded-xl border border-red-500/30 text-red-400 py-2.5 text-sm">Turn off App Lock</button>
          </div>
        )}

        <button onClick={toggleHide} data-testid="hide-previews-toggle" className="w-full flex items-center gap-3 rounded-2xl bg-[var(--rc-bg)] border border-[var(--rc-border)] p-4 text-left">
          <span className="h-9 w-9 grid place-items-center rounded-full bg-[var(--rc-surface-2)] text-[var(--rc-gold)]"><EyeOff size={18} /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium">Hide message previews</span>
            <span className="block text-xs text-[var(--rc-muted)]">Hide last-message text in the chat list</span>
          </span>
          <span className={`w-11 h-6 rounded-full relative transition-colors ${hide ? "bg-[var(--rc-gold)]" : "bg-[var(--rc-surface-2)]"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${hide ? "left-[22px]" : "left-0.5"}`} />
          </span>
        </button>
      </div>
    </Modal>
  );
}
